import { useState } from "react";
import { signGcRequirements } from "../services/projectService";

interface ApiError {
  message?: string;
}

interface SigData {
  dataUrl: string;
  fullName: string;
  designation: string;
  companyName: string;
}

interface SubmitRequirementsParams {
  acknowledgedIds: Set<string>;
  allIds?: string[];
  sigData?: SigData;
  publish?: boolean;
}

interface RequirementPayloadItem {
  id: string;
  isAccepted: "yes" | "no";
}

interface SubmitPayload {
  requirements: RequirementPayloadItem[];
  gcSignatureUrl?: string;
  fullName?: string;
  designation?: string;
  companyName?: string;
}

type SubmitResult = { success: true } | { success: false; message: string };

const useSubmitGcRequirements = (projectId: string = "") => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submitRequirements = async ({
    acknowledgedIds,
    allIds,
    sigData,
    publish = true,
  }: SubmitRequirementsParams): Promise<SubmitResult> => {
    setLoading(true);
    setError("");

    const ackedSet = new Set(acknowledgedIds);
    const ids: string[] = allIds ?? [...acknowledgedIds];

    const payload: SubmitPayload = {
      requirements: ids.map((id) => ({
        id,
        isAccepted: ackedSet.has(id) ? "yes" : "no",
      })),
      ...(sigData && {
        gcSignatureUrl: sigData.dataUrl,
        fullName: sigData.fullName,
        designation: sigData.designation,
        companyName: sigData.companyName,
      }),
    };

    const { error: apiError } = (await signGcRequirements(
      projectId,
      payload,
      publish,
    )) as {
      error: ApiError | null;
    };
    setLoading(false);

    if (apiError) {
      const msg = apiError.message || "Failed to submit requirements.";
      setError(msg);
      return { success: false, message: msg };
    }

    return { success: true };
  };

  return { submitRequirements, loading, error };
};

export default useSubmitGcRequirements;
